import { formatDistanceToNowStrict, isAfter, isBefore } from 'date-fns'
import { es } from 'date-fns/locale'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import type { AppProfile, Selection, WeekBundle, WeekStatus } from '@/types'

export const APP_TIMEZONE = 'America/Argentina/Buenos_Aires'

export function cn(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ')
}

export function formatDate(value: string | Date, pattern = 'dd/MM/yyyy'): string {
  const normalized = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? localDateAtNoon(value)
    : value
  return formatInTimeZone(normalized, APP_TIMEZONE, pattern, { locale: es })
}

export function formatLongDate(value: string | Date): string {
  return capitalize(formatDate(value, "EEEE d 'de' MMMM"))
}

export function formatShortDay(value: string | Date): string {
  return capitalize(formatDate(value, 'EEE d'))
}

export function formatDeadline(value: string): string {
  return formatDate(value, "EEEE d 'de' MMMM 'a las' HH:mm")
}

export function formatWeekRange(week: Pick<WeekBundle, 'startDate' | 'endDate'>): string {
  const startMonth = formatDate(week.startDate, 'MMMM')
  const endMonth = formatDate(week.endDate, 'MMMM')
  const start = formatDate(week.startDate, 'd')
  const end = formatDate(week.endDate, 'd')
  return startMonth === endMonth
    ? `${start} al ${end} de ${endMonth}`
    : `${start} de ${startMonth} al ${end} de ${endMonth}`
}

export function localDateAtNoon(date: string): Date {
  return fromZonedTime(`${date}T12:00:00`, APP_TIMEZONE)
}

export function deadlinePassed(week: Pick<WeekBundle, 'selectionDeadline'>, now = new Date()): boolean {
  return !isBefore(now, new Date(week.selectionDeadline))
}

export function deadlineIsClose(week: Pick<WeekBundle, 'selectionDeadline'>, hours = 24): boolean {
  const deadline = new Date(week.selectionDeadline)
  const now = new Date()
  const threshold = new Date(now.getTime() + hours * 60 * 60 * 1000)
  return isAfter(deadline, now) && isBefore(deadline, threshold)
}

export function effectiveWeekStatus(week: Pick<WeekBundle, 'status' | 'selectionDeadline'>): WeekStatus {
  if (week.status === 'open' && deadlinePassed(week)) return 'closed'
  return week.status
}

export function timeUntilDeadline(deadline: string): string {
  const date = new Date(deadline)
  if (date <= new Date()) return 'El período de elección finalizó'
  return `Cierra en ${formatDistanceToNowStrict(date, { locale: es })}`
}

export function capitalize(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value
}

export function initials(profile: Pick<AppProfile, 'firstName' | 'lastName'>): string {
  return `${profile.firstName.charAt(0)}${profile.lastName.charAt(0)}`.toUpperCase()
}

export function fullName(profile: Pick<AppProfile, 'firstName' | 'lastName'>): string {
  return `${profile.firstName} ${profile.lastName}`.trim()
}

export function isWeekSelectionComplete(
  week: WeekBundle,
  selections: Selection[],
  employeeId: string,
): boolean {
  const requiredDays = week.days.filter((day) => day.serviceEnabled)
  const chosen = new Set(
    selections
      .filter((selection) => selection.employeeId === employeeId && selection.weekId === week.id)
      .map((selection) => selection.weekDayId),
  )
  return requiredDays.length > 0 && requiredDays.every((day) => chosen.has(day.id))
}

export function safeErrorMessage(error: unknown, fallback = 'Ocurrió un error. Intentá nuevamente.'): string {
  if (import.meta.env.DEV) console.error(error)
  if (error instanceof Error) {
    const text = error.message.toLowerCase()
    if (text.includes('invalid login') || text.includes('credentials')) return 'Usuario o contraseña incorrectos.'
    if (text.includes('disabled') || text.includes('inactive')) return 'Tu cuenta se encuentra deshabilitada.'
    if (text.includes('deadline') || text.includes('closed')) return 'El período de elección ya finalizó.'
    if (text.includes('duplicate') || text.includes('23505')) return 'Ya existe un registro con esos datos.'
    if (text.includes('network') || text.includes('fetch')) return 'No pudimos conectarnos. Revisá tu conexión e intentá nuevamente.'
  }
  return fallback
}
